import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Bell, Send, Loader } from 'lucide-react'
import { getUsers, sendNotification } from '../../services/staffServices'
import StaffLayout from './components/StaffLayout'
import toast from 'react-hot-toast'

const TYPES = [
    { value: 'general', label: 'General' },
    { value: 'task_assigned', label: 'Task Assigned' },
    { value: 'task_status_changed', label: 'Task Status Changed' },
    { value: 'task_completed', label: 'Task Completed' },
]

export default function Notifications() {
    const [form, setForm] = useState({ type: 'general', title: '', message: '', broadcast: true, user_id: '' })
    const [sending, setSending] = useState(false)
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

    const { data: users = [] } = useQuery({ queryKey: ['users'], queryFn: getUsers })

    const handleSend = async (e) => {
        e.preventDefault()
        if (!form.title || !form.message) { toast.error('Title and message required'); return }
        if (!form.broadcast && !form.user_id) { toast.error('Select a user or use broadcast'); return }
        setSending(true)
        try {
            const payload = {
                type: form.type,
                broadcast: form.broadcast,
                user_id: form.broadcast ? undefined : form.user_id,
                data: { title: form.title, message: form.message },
            }
            const result = await sendNotification(payload)
            toast.success(`Sent to ${result?.successCount || 0} device(s)`)
            setForm({ type: 'general', title: '', message: '', broadcast: true, user_id: '' })
        } catch (err) {
            toast.error(err.message || 'Failed to send')
        } finally {
            setSending(false)
        }
    }

    return (
        <StaffLayout>
            <div className="max-w-lg space-y-5">
                <div>
                    <h1 className="text-xl font-bold text-white">Push Notifications</h1>
                    <p className="text-slate-400 text-sm mt-0.5">Send notifications to staff devices</p>
                </div>

                <div className="bg-slate-900 rounded-2xl border border-white/5 p-6">
                    <form onSubmit={handleSend} className="space-y-4">
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Notification Type</label>
                            <select value={form.type} onChange={e => set('type', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500">
                                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>

                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Title *</label>
                            <input value={form.title} onChange={e => set('title', e.target.value)}
                                placeholder="Notification title"
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>

                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Message *</label>
                            <textarea value={form.message} onChange={e => set('message', e.target.value)} rows={3}
                                placeholder="Notification message..."
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 resize-none" />
                        </div>

                        <div>
                            <label className="text-xs text-slate-400 block mb-2">Recipients</label>
                            <div className="flex gap-3">
                                <button type="button" onClick={() => set('broadcast', true)}
                                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition ${form.broadcast ? 'bg-blue-600 border-blue-600 text-white' : 'bg-slate-800 border-white/8 text-slate-400 hover:text-white'}`}>
                                    All Staff (Broadcast)
                                </button>
                                <button type="button" onClick={() => set('broadcast', false)}
                                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition ${!form.broadcast ? 'bg-blue-600 border-blue-600 text-white' : 'bg-slate-800 border-white/8 text-slate-400 hover:text-white'}`}>
                                    Specific Staff
                                </button>
                            </div>
                        </div>

                        {!form.broadcast && (
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Select Staff</label>
                                <select value={form.user_id} onChange={e => set('user_id', e.target.value)}
                                    className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500">
                                    <option value="">Choose staff member</option>
                                    {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                                </select>
                            </div>
                        )}

                        <button type="submit" disabled={sending}
                            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white py-3 rounded-xl text-sm font-medium transition flex items-center justify-center gap-2">
                            {sending ? <><Loader size={16} className="animate-spin" /> Sending...</> : <><Send size={16} /> Send Notification</>}
                        </button>
                    </form>
                </div>

                <div className="bg-slate-900/50 rounded-xl border border-white/5 p-4">
                    <div className="flex items-start gap-3">
                        <Bell size={16} className="text-blue-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-slate-400 leading-relaxed">
                            Notifications are sent via Firebase Cloud Messaging to all devices where staff are logged in to the StaffSync app. Broadcast sends to all active devices.
                        </p>
                    </div>
                </div>
            </div>
        </StaffLayout>
    )
}
